import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CookieService } from 'ngx-cookie-service';
import { Subscription } from 'rxjs';
import { WebKitDocument, WebKitElement } from 'src/app/models/webkit.model';
import { SiteModel } from 'src/app/models/site.model';
import { ImageService } from 'src/app/services/image.service';
import { Icons } from '../../../../app/models/icons.model';
import { AppService } from '../../../services/app.service';
import { HelpModalComponent } from '../helpmodal/helpmodal.component';

@Component({
  selector: 'app-viewer-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  navCollapsed: boolean = true;
  icons = new Icons();

  // event handlers
  imageLoadingEvent: Subscription = null;
  imageLoadedEvent: Subscription = null;

  get isFullscreen(): boolean {
    return (
      !!document.fullscreenElement ||
      (<WebKitDocument>document).webkitIsFullScreen
    );
  }

  get canFullscreen(): boolean {
    return (
      (<WebKitDocument>document).fullscreenEnabled ||
      (<WebKitDocument>document).webkitFullscreenEnabled ||
      (<WebKitDocument>document).mozFullscreenEnabled
    );
  }

  @ViewChild('siteDropdown')
  siteDropdown: NgbDropdown;

  @ViewChild('cameraDropdown')
  cameraDropdown: NgbDropdown;

  /**
   * Header component constructor
   */
  constructor(
    public appService: AppService,
    private modalService: NgbModal,
    private cookieService: CookieService,
    private imageService: ImageService
  ) { }

  ngOnInit(): void {
    this.imageLoadedEvent = this.imageService.imageLoadedEvent.subscribe(() =>
      this.onImageLoaded()
    );
    this.imageLoadingEvent = this.imageService.imageLoadingEvent.subscribe(() =>
      this.onImageLoading()
    );
  }

  ngOnDestroy(): void {
    if (this.imageLoadedEvent) this.imageLoadedEvent.unsubscribe();
    if (this.imageLoadingEvent) this.imageLoadingEvent.unsubscribe();
  }

  private onImageLoading() {
    if (this.appService.doAutoRefresh) this.closeNav();
  }

  private onImageLoaded() { }

  /**
   * Change the auto-refresh settings by invoking the corresponding method in the AppService and then closing the navigation menu
   */
  changeAutoRefreshSettings(interval: number) {
    this.appService.configureAutoRefresh(interval);
    this.closeNav();
  }

  /**
   * Toggle the navigation menu by inverting the value of the navCollapsed property, which can be used to control the visibility of the menu in the template
   */
  toggleNav() {
    this.navCollapsed = !this.navCollapsed;
  }

  /**
   * Close the navigation menu by setting the navCollapsed property to true, which can be used to control the visibility of the menu in the template
   */
  closeNav() {
    this.navCollapsed = true;
  }

  /**
   * Open the help modal dialog using the NgbModal service, centering it on the screen and allowing it to be closed with the keyboard
   */
  showHelpModal() {
    this.modalService.open(HelpModalComponent, {
      centered: true,
      keyboard: true,
    });
  }

  /**
   * Start the site status check process by invoking the corresponding method in the AppService
   */
  startStatusCheck() {
    this.appService.startSiteStatusCheck();
  }

  getSiteCamerasStatusClass(site: SiteModel): string {
    const cameras = site?.Cameras ?? [];
    // if (cameras.length === 0) return 'text-warning';

    const anyCameraError = cameras.some((camera) => camera?.HasError === true);
    return anyCameraError ? 'text-warning ' : 'text-success';
  }

  getSiteCamerasStatusTitle(site: SiteModel): string {
    const cameras = site?.Cameras ?? [];
    // if (cameras.length === 0) return 'No cameras found for site';

    const anyCameraError = cameras.some((camera) => camera?.HasError === true);
    return anyCameraError
      ? 'One or more cameras have errors'
      : 'All cameras are healthy';
  }

  /**
   * Enter fullscreen mode for the camera viewer element using the appropriate method based on the browser's API support
   */
  enterFullscreen() {
    let elem = <WebKitElement>document.getElementById('camera-viewer');

    if (elem) {
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    }
  }

  /**
   * Exit fullscreen mode using the appropriate method based on the browser's API support
   * This method checks for the standard exitFullscreen method as well as vendor-specific implementations for WebKit and Mozilla browsers to ensure compatibility across different platforms
   */
  exitFullscreen() {
    if (document.exitFullscreen) document.exitFullscreen();
    else if ((<WebKitDocument>document).webkitCancelFullScreen)
      (<WebKitDocument>document).webkitCancelFullScreen();
    else if ((<WebKitDocument>document).mozCancelFullScreen)
      (<WebKitDocument>document).mozCancelFullScreen();
  }

  /**
   * Handle global keypress events for site and camera navigation, image refresh, and fullscreen toggling
   * This method listens for specific key presses and performs corresponding actions such as navigating between sites and cameras, refreshing the image, and toggling fullscreen mode
   * The key bindings are designed to work without any modifier keys (Alt, Ctrl, Meta) to allow for quick access to these functions
   */
  @HostListener('document:keyup', ['$event'])
  handleKeypress(event: KeyboardEvent) {
    // Keys without modifiers
    if (
      this.appService.isAppLoaded &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      switch (event.key?.toLowerCase()) {
        case 'w':
        case 'arrowup':
          this.appService.nextSite();
          break;
        case 's':
        case 'arrowdown':
          this.appService.previousSite();
          break;
        case 'arrowleft':
        case 'a':
          // this.closeNav();
          this.appService.previousCamera();
          break;
        case 'arrowright':
        case 'd':
          // this.closeNav();
          this.appService.nextCamera();
          break;
        case 'r':
          // this.closeNav();
          this.appService.refreshCamera(true);
          break;
        case 'f':
          if (!this.isFullscreen) this.enterFullscreen();
          else this.exitFullscreen();
          break;
      }
    }
  }

  /**
   * Toggle the auto-change site setting and update the corresponding cookie
   * This setting determines whether the application will automatically change the site based on certain conditions
   * The cookie is set to persist for 999 days and is configured with 'Lax' same-site policy for security
   */
  changeAutoSettings() {
    this.cookieService.set(
      'autoChangeSite',
      this.appService.autoChangeSite.toString(),
      999,
      null,
      null,
      false,
      'Lax'
    );
  }
}
